import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Clock, Mail, Eye, ExternalLink } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Switch } from '../../../../ui/switch';
import { Badge } from '../../../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../ui/dialog';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';
import type { Sequence, SequenceStep, SequenceKey, EmailTemplateV2 } from '../../../../../types/epicengage';
import { SEQUENCE_INFO } from '../../../../../types/epicengage';

const SEQUENCE_ICONS: Record<SequenceKey, string> = {
  NEW_CUSTOMER_ONBOARDING: '👋',
  POST_ACTION_FOLLOWUP: '🎉',
  INACTIVE_30D: '💤'
};

interface SequenceWithSteps extends Sequence {
  steps: (SequenceStep & { template?: EmailTemplateV2 })[];
}

export default function Automations() {
  const navigate = useNavigate();
  const { merchant } = useMerchant();
  const [sequences, setSequences] = useState<SequenceWithSteps[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewEmailsSequence, setViewEmailsSequence] = useState<SequenceWithSteps | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplateV2 | null>(null);

  const fetchSequences = async () => {
    if (!merchant) return;

    const { data: seqData, error } = await epicengageSupabase
      .from('sequences')
      .select(`
        *,
        sequence_steps (
          *,
          email_templates_v2 (*)
        )
      `)
      .eq('merchant_id', merchant.id)
      .order('key');

    if (error) {
      console.error('Error fetching sequences:', error);
      return;
    }

    // Map steps with template info
    const mapped = (seqData || []).map(seq => ({
      ...seq,
      steps: (seq.sequence_steps || []).sort((a: any, b: any) => a.step_order - b.step_order).map((step: any) => ({
        ...step,
        template: step.email_templates_v2
      }))
    })) as SequenceWithSteps[];

    setSequences(mapped);
    setLoading(false);
  };

  useEffect(() => {
    fetchSequences();
  }, [merchant]);

  const toggleSequence = async (sequence: Sequence) => {
    const { error } = await epicengageSupabase
      .from('sequences')
      .update({ enabled: !sequence.enabled })
      .eq('id', sequence.id);

    if (error) {
      toast.error('Failed to update sequence');
      return;
    }

    toast.success(sequence.enabled ? 'Automation disabled' : 'Automation enabled');
    fetchSequences();
  };

  const getDelayLabel = (hours: number) => {
    if (hours === 0) return 'Immediately';
    if (hours < 24) return `${hours} hours`;
    return `Day ${Math.floor(hours / 24)}`;
  };

  const hasAllTemplates = (sequence: SequenceWithSteps) => {
    return sequence.steps.length > 0 && sequence.steps.every(step => step.template_id);
  };

  return (
    <V1Layout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Zap className="h-8 w-8" />
            Automations
          </h1>
          <p className="text-muted-foreground mt-1">
            Automatic emails triggered by customer events. Toggle to activate.
          </p>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-6">
            {sequences.map(sequence => {
              // Always show expected email count even if steps don't exist yet
              const expectedEmails = SEQUENCE_INFO[sequence.key as SequenceKey]?.steps || sequence.steps.length;
              const actualEmails = sequence.steps.length;
              const displayEmailCount = actualEmails > 0 ? actualEmails : expectedEmails;
              
              return (
                <Card key={sequence.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{SEQUENCE_ICONS[sequence.key as SequenceKey]}</span>
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {SEQUENCE_INFO[sequence.key as SequenceKey]?.name || sequence.key}
                            {sequence.enabled ? (
                              <Badge variant="default" className="bg-green-500">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Ready</Badge>
                            )}
                          </CardTitle>
                          <CardDescription>
                            {SEQUENCE_INFO[sequence.key as SequenceKey]?.description}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setViewEmailsSequence(sequence)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View emails
                        </Button>
                        <Switch
                          checked={sequence.enabled}
                          onCheckedChange={() => toggleSequence(sequence)}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Always show email count - never show 0 */}
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>{displayEmailCount} email{displayEmailCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="text-green-600">
                        ✓ Emails ready. Toggle to activate.
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* View Emails Modal */}
        <Dialog open={!!viewEmailsSequence} onOpenChange={() => setViewEmailsSequence(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {viewEmailsSequence && SEQUENCE_ICONS[viewEmailsSequence.key as SequenceKey]}
                {viewEmailsSequence && SEQUENCE_INFO[viewEmailsSequence.key as SequenceKey]?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {viewEmailsSequence?.steps.map((step) => (
                <div 
                  key={step.id}
                  className="flex items-center justify-between p-4 bg-muted rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm min-w-[80px]">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{getDelayLabel(step.delay_hours)}</span>
                    </div>
                    <div>
                      <p className="font-medium">{step.template?.name || 'Email'}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-xs">
                        {step.template?.subject || 'No subject'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => step.template && setPreviewTemplate(step.template)}
                      disabled={!step.template}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        if (step.template) {
                          if (step.template.editor_type === 'GRAPESJS') {
                            navigate(`/clientpanel/enage/templates/${step.template.id}/grapesjs`);
                          } else {
                            navigate(`/clientpanel/enage/templates/${step.template.id}/edit`);
                          }
                        }
                      }}
                      disabled={!step.template}
                    >
                      Edit
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{previewTemplate?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Subject</p>
                <p className="text-lg">{previewTemplate?.subject}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Body</p>
                {previewTemplate?.html_cache ? (
                  <div 
                    className="border rounded-lg p-4 bg-white"
                    dangerouslySetInnerHTML={{ __html: previewTemplate.html_cache }}
                  />
                ) : previewTemplate?.body_json?.text ? (
                  <div className="border rounded-lg p-4 bg-white whitespace-pre-wrap">
                    {previewTemplate.body_json.text}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No content</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </V1Layout>
  );
}
